import { useState } from 'react';
import type { BlockedEvent, RelaunchOutcome } from '../api/types.js';
import { useBlockedEvents, useRelaunchBlocked } from '../hooks/use-reviews.js';
import { Button } from './button.js';
import { timeAgo } from '../utils/time-ago.js';

// Object events refused for their trigger's daily budget. The queue retries each one every 15 minutes, so it runs on
// its own once the budget is raised or renewed at midnight UTC; « Relaunch » runs it now, past the queue. The one
// visual device is the budget bar: how far the trigger went past its limit, the reason the event waits.

const KIND: Record<BlockedEvent['kind'], string> = {
  review: 'Pull request review',
  ticket: 'Ticket',
  deployment: 'Deployment analysis',
  event: 'Event',
};

/** 5240000 → 5.24M, 5000000 → 5M, 12500 → 12.5k */
function tokens(n: number): string {
  const trim = (x: number) => x.toFixed(2).replace(/\.?0+$/, '');
  if (n >= 1_000_000) return `${trim(n / 1_000_000)}M`;
  if (n >= 1_000) return `${trim(n / 1_000)}k`;
  return String(n);
}

/** The trigger's spend against its limit: the limit is the notch, the part past it is in red */
function BudgetBar({ used, limit }: { used: number; limit: number }) {
  const scale = Math.max(used, limit, 1);
  const within = (Math.min(used, limit) / scale) * 100;
  const over = (Math.max(0, used - limit) / scale) * 100;
  return (
    <div aria-hidden="true" className="relative mt-1.5 h-1.5 w-full max-w-sm overflow-visible rounded-full" style={{ backgroundColor: 'var(--color-surface-2)' }}>
      <div className="absolute inset-y-0 left-0 rounded-l-full" style={{ width: `${within}%`, backgroundColor: 'var(--color-warn)' }} />
      {over > 0 && <div className="absolute inset-y-0 rounded-r-full" style={{ left: `${within}%`, width: `${over}%`, backgroundColor: 'var(--color-error)' }} />}
      <div className="absolute -inset-y-1 w-px" style={{ left: `${(limit / scale) * 100}%`, backgroundColor: 'var(--color-text-2)' }} />
    </div>
  );
}

/** What the relaunch did, in one sentence, with the numbers when the budget still refuses it */
function outcomeText(o: RelaunchOutcome, kind: BlockedEvent['kind']): { text: string; tone: 'ok' | 'warn' | 'error' } {
  const what = kind === 'review' ? 'The review' : kind === 'deployment' ? 'The analysis' : 'The event';
  switch (o.outcome) {
    case 'running':
      return { text: `Running now. ${what} appears here when it is ready.`, tone: 'ok' };
    case 'starting':
      return { text: 'The server is starting: the event runs as soon as it is up.', tone: 'ok' };
    case 'handled':
      if (o.status === 'duplicate') return { text: 'Already handled: it ran from the queue meanwhile.', tone: 'ok' };
      if (o.status === 'held') return { text: 'Held by the injection check: run or drop it below.', tone: 'warn' };
      if (o.status === 'superseded') return { text: 'Nothing to run: a newer event replaces this one.', tone: 'ok' };
      return { text: o.message, tone: 'ok' };
    case 'over_budget':
      return {
        text: `Still over budget: ${tokens(o.used)} of ${tokens(o.limit)} tokens used today by ${o.trigger}. Raise the limit in ${o.parameter}, then relaunch.`,
        tone: 'error',
      };
    case 'busy':
      return { text: `Not now: ${o.message}. Relaunch in a minute, or let the queue run it.`, tone: 'warn' };
  }
}

const TONE = { ok: 'var(--color-success)', warn: 'var(--color-warn)', error: 'var(--color-error)' } as const;

type Result = { text: string; tone: keyof typeof TONE };

function BlockedRow({ serverId, event, isAdmin, result, onResult }: {
  serverId: string; event: BlockedEvent; isAdmin: boolean; result?: Result; onResult: (r: Result | null) => void;
}) {
  const relaunch = useRelaunchBlocked(serverId);
  const retries = event.attempts - 1;
  const titleId = `blocked-${event.externalRef.replace(/[^a-z0-9]/gi, '-')}`;

  function run() {
    onResult(null);
    relaunch.mutate(event.externalRef, {
      onSuccess: (o) => onResult(outcomeText(o, event.kind)),
      onError: (err) => onResult({
        text: /not found|no event held/i.test((err as Error).message)
          ? 'Already gone: it ran or expired since this page loaded.'
          : `Could not relaunch: ${(err as Error).message}`,
        tone: 'error',
      }),
    });
  }

  return (
    <li className="card min-w-0 px-4 py-3" style={{ borderLeft: '3px solid var(--color-warn)' }}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p id={titleId} className="min-w-0 text-sm">
          <span style={{ color: 'var(--color-text-3)' }}>{KIND[event.kind]}</span>{' '}
          {event.link
            ? <a href={event.link} target="_blank" rel="noreferrer" className="break-words font-medium hover:underline" style={{ color: 'var(--color-text)' }}>{event.label}</a>
            : <span className="break-words font-medium" style={{ color: 'var(--color-text)' }}>{event.label}</span>}
        </p>
        <span className="flex-shrink-0 text-xs" style={{ color: 'var(--color-text-3)' }}>
          refused <time dateTime={event.refusedAt}>{timeAgo(event.refusedAt)}</time>
          {retries > 0 && `, retried ${retries === 1 ? 'once' : `${retries} times`}`}
        </span>
      </div>
      <p className="mt-2 text-xs tabular-nums" style={{ color: 'var(--color-text-2)' }}>
        <span style={{ color: 'var(--color-text)' }}>{tokens(event.used)}</span> of {tokens(event.limit)} tokens used today by {event.trigger}
      </p>
      <BudgetBar used={event.used} limit={event.limit} />
      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-2">
        {isAdmin && (
          <Button size="sm" variant="primary" disabled={relaunch.isPending} onClick={run} aria-describedby={titleId}>
            {relaunch.isPending ? 'Relaunching…' : 'Relaunch'}
          </Button>
        )}
        <p role="status" className="min-w-0 flex-1 text-xs" style={{ color: result ? TONE[result.tone] : 'var(--color-text-3)' }}>
          {result?.text ?? (isAdmin ? '' : 'An admin can relaunch it.')}
        </p>
      </div>
    </li>
  );
}

export function BudgetHeld({ serverId, isAdmin }: { serverId: string; isAdmin: boolean }) {
  const blocked = useBlockedEvents(serverId);
  // Outcomes kept here: a relaunched event leaves the list once it runs, its outcome stays readable
  const [results, setResults] = useState<Record<string, { label: string; result: Result }>>({});
  const list = blocked.data ?? [];
  const listed = new Set(list.map((e) => e.externalRef));
  const gone = Object.entries(results).filter(([ref]) => !listed.has(ref));
  // Nothing held: no empty section. A failed load says so rather than looking empty.
  if (!list.length && !gone.length && !blocked.error) return null;
  const record = (event: BlockedEvent) => (r: Result | null) => setResults((prev) => {
    const next = { ...prev };
    if (r) next[event.externalRef] = { label: `${KIND[event.kind]} ${event.label}`, result: r };
    else delete next[event.externalRef];
    return next;
  });
  return (
    <section aria-labelledby="budget-held-title" className="mb-8 min-w-0">
      <h2 id="budget-held-title" className="text-lg font-semibold" style={{ color: 'var(--color-text)' }}>Held by the daily budget</h2>
      <p className="mt-0.5 max-w-prose text-sm" style={{ color: 'var(--color-text-3)' }}>
        Their trigger spent today's token budget. Each one is retried every 15 minutes and runs once the budget is raised or
        renewed at midnight UTC. Relaunch runs it now, if the budget allows.
      </p>
      {blocked.error && (
        <p role="alert" className="mt-3 text-sm" style={{ color: 'var(--color-error)' }}>
          Could not load the events held by the budget: {(blocked.error as Error).message}. They refresh every minute.
        </p>
      )}
      {(list.length > 0 || gone.length > 0) && (
        <ul className="mt-4 space-y-2">
          {list.map((e) => (
            <BlockedRow key={e.externalRef} serverId={serverId} event={e} isAdmin={isAdmin} result={results[e.externalRef]?.result} onResult={record(e)} />
          ))}
          {gone.map(([ref, { label, result }]) => (
            <li key={ref} className="card min-w-0 px-4 py-3" style={{ borderLeft: `3px solid ${TONE[result.tone]}` }}>
              <p className="text-sm" style={{ color: 'var(--color-text)' }}>{label}</p>
              <p role="status" className="mt-1 text-xs" style={{ color: TONE[result.tone] }}>{result.text}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
